import type { BindResult, CogoJsonTemplate, IterationFrame, Resolvers } from "./types"
import { evaluateExpression } from "./expression-evaluator"

/**
 * Walk a Cogo JSON template and substitute string values per the 5-namespace contract.
 *
 * Pure function — no I/O, runs in WASM / browser context. See pilot docs/design-system/pilots/product-card-v1.md §6.
 */
export function bindExample(template: CogoJsonTemplate, resolvers: Resolvers): BindResult {
  const warnings: string[] = []
  const output = walk(template, resolvers, warnings)
  return { output, warnings }
}

function walk(node: unknown, r: Resolvers, warnings: string[]): unknown {
  if (Array.isArray(node)) return node.map((n) => walk(n, r, warnings))
  if (node && typeof node === "object") {
    const asRecord = node as Record<string, unknown>
    // Iteration expansion pass (pricing-tier-v1.md §6 Step A).
    //
    // A node carrying `iterateOver: "#{...array expr...}"` clones its `children` subtree once per
    // array element, injecting `__item` (element) + `__index` (number) into a sub-frame for the
    // cloned subtree. The resulting node has `iterateOver` stripped and `children` replaced with
    // the expanded list. Iteration is evaluated BEFORE descending into children so that outer
    // frames (`__bind`, nested `__item` shadowing, etc.) are already resolved on the iterateOver
    // expression itself.
    if (typeof asRecord.iterateOver === "string") {
      return expandIteration(asRecord, r, warnings)
    }
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(asRecord)) {
      // `visibleIf` is a runtime-evaluated expression (conditional render predicate). The binder
      // does NOT evaluate it — it preserves the string verbatim so the runtime renderer can.
      // Matches the "PoC binder preserves conditional rendering expressions as-is" contract from
      // docs/design-system/pilots/hero-section-v1.md §6 and the task spec.
      if (k === "visibleIf" && typeof v === "string") {
        out[k] = v
        continue
      }
      out[k] = walk(v, r, warnings)
    }
    // Recipe conformance check runs AFTER children are resolved so dynamic variant/size
    // expressions (e.g. `#{__bind.tier.variant}`) are checked on their resolved values, not on
    // the raw expression string. Graceful degrade: no recipe resolver, no recipe for this type,
    // or no availableVariants/Sizes on the recipe → skip the check silently.
    checkRecipeConformance(out, r, warnings)
    return out
  }
  if (typeof node !== "string") return node
  return substituteString(node, r, warnings)
}

/**
 * Expand `iterateOver` on a container node.
 *
 *   1. Evaluate the `iterateOver` expression in the CURRENT resolver frame (so the expression can
 *      reference the enclosing `__bind` / outer `__item`).
 *   2. If the result is not an array, emit a warning and return the container with empty children.
 *   3. Otherwise walk the `children` subtree once per element with an inner `__iter` frame that
 *      shadows the outer one (via `parent`). Non-`children` attributes are walked ONCE in the
 *      outer frame (container-level attrs, e.g. `className`, are not per-item).
 *
 * The emitted container has `iterateOver` stripped — the caller sees a fully materialized
 * `children: [...]` array ready to render.
 */
function expandIteration(
  node: Record<string, unknown>,
  r: Resolvers,
  warnings: string[],
): Record<string, unknown> {
  const expr = node.iterateOver as string
  const arr = evaluateExpression(expr, r, warnings)

  // Build the container's non-child attributes in the outer frame (no __iter yet).
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(node)) {
    if (k === "iterateOver") continue
    if (k === "children") continue
    if (k === "visibleIf" && typeof v === "string") {
      out[k] = v
      continue
    }
    out[k] = walk(v, r, warnings)
  }

  if (!Array.isArray(arr)) {
    // PASS-THROUGH: the expression returned a STRING — either a `#{...}` runtime reference
    // (e.g. an `__app` pass-through per pilot §6) or a bare path literal that the corpus
    // still carries (e.g. `"login.announcements"` — legacy pre-`#{}` shape). In both cases
    // the binder defers expansion: emit the node with `iterateOver` intact + children walked
    // as a TEMPLATE (static __bind references inside children still resolve, but
    // `__item` / `__index` survive verbatim for the runtime renderer to expand against live
    // state).
    if (typeof arr === "string") {
      out.iterateOver = arr
      const childrenTpl = node.children
      if (Array.isArray(childrenTpl)) {
        const innerResolvers: Resolvers = { ...r, __iterDeferred: true }
        out.children = childrenTpl.map((c) => walk(c, innerResolvers, warnings))
      } else {
        out.children = childrenTpl as unknown
      }
      return out
    }
    if (arr !== undefined) {
      warnings.push(
        `iterateOver expression did not resolve to an array: ${expr} → ${typeof arr}`,
      )
    } else {
      warnings.push(`iterateOver expression unresolved: ${expr}`)
    }
    out.children = []
    return out
  }

  const childrenTemplate = node.children
  if (!Array.isArray(childrenTemplate)) {
    // Empty subtree to iterate — still emit an empty children array so the output shape is
    // predictable for downstream renderers.
    out.children = []
    return out
  }

  const expanded: unknown[] = []
  for (let index = 0; index < arr.length; index++) {
    const innerFrame: IterationFrame = {
      item: arr[index],
      index,
      parent: r.__iter,
    }
    const innerResolvers: Resolvers = { ...r, __iter: innerFrame }
    // Clone each child template per element. `walk` handles nested iterateOver so arrays of
    // arrays / nested iteration work via recursion on this same expansion pass.
    for (const child of childrenTemplate) {
      expanded.push(walk(child, innerResolvers, warnings))
    }
  }
  out.children = expanded
  return out
}

/**
 * Enforce `cogo:SemanticTypeRecipeConformanceShape` (pilot §7): if the node has a `type` plus a
 * `variant` or `size`, and the caller supplied a `__recipe` resolver with a registered recipe whose
 * `availableVariants` / `availableSizes` enumerates the allowed values, any resolved value outside
 * the enum emits a non-fatal warning. Mismatch does NOT reject the bind — render can still proceed
 * (runtime renderer may fall back to `"default"`), but the warning is surfaced to the caller.
 */
function checkRecipeConformance(
  out: Record<string, unknown>,
  r: Resolvers,
  warnings: string[],
): void {
  if (!r.__recipe) return
  const type = out.type
  if (typeof type !== "string" || type.length === 0) return
  // Only fetch the recipe when there's actually something to check.
  const hasVariant = typeof out.variant === "string"
  const hasSize = typeof out.size === "string"
  if (!hasVariant && !hasSize) return
  const recipe = r.__recipe.getRecipe(type)
  if (!recipe) return
  if (hasVariant && Array.isArray(recipe.availableVariants)) {
    const v = out.variant as string
    if (!recipe.availableVariants.includes(v)) {
      warnings.push(
        `variant '${v}' not in availableVariants for type '${type}'; ` +
          `expected one of [${recipe.availableVariants.join(", ")}]`,
      )
    }
  }
  if (hasSize && Array.isArray(recipe.availableSizes)) {
    const s = out.size as string
    if (!recipe.availableSizes.includes(s)) {
      warnings.push(
        `size '${s}' not in availableSizes for type '${type}'; ` +
          `expected one of [${recipe.availableSizes.join(", ")}]`,
      )
    }
  }
}

/**
 * String substitution. Two cases, in priority order:
 *
 *  1. **Token-wise** — string contains `@ds:` (optionally alongside `#{...}` expressions AND/OR
 *     plain literal utility tokens). Whitespace-split into tokens; each `@ds:X` is resolved via the
 *     DS resolver, each whole-token `#{...}` via the expression evaluator, plain literals pass
 *     through. Tokens rejoined with a single space. Multiple internal spaces collapse to one
 *     (acceptable for className, which is whitespace-insensitive).
 *
 *     This unified path subsumes three configurations:
 *       - pure `@ds:` list (e.g. `"@ds:card-base @ds:card-hover"`)
 *       - `@ds:` + plain literal (e.g. `"@ds:card-base w-full"`)
 *       - `@ds:` + `#{...}` (e.g. `"@ds:card-base #{__bind.extra}"`)
 *       - all three together.
 *
 *  2. **Expression-only** — `#{...}` somewhere in the string, no `@ds:` anywhere → delegate to the
 *     expression evaluator. Preserves non-string return values (numbers/objects/undefined) when the
 *     whole string is a single `#{...}`.
 *
 * Otherwise the literal string is returned unchanged.
 */
function substituteString(s: string, r: Resolvers, warnings: string[]): unknown {
  if (s.includes("@ds:")) {
    return s
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((tok) => resolveToken(tok, r, warnings))
      .join(" ")
  }

  if (s.includes("#{")) {
    const result = evaluateExpression(s, r, warnings)
    // evaluateExpression returns the raw resolver value when the whole string is one #{...}.
    // Leave it as-is so JSON serialization sees objects/numbers/undefined correctly.
    return result
  }

  return s
}

/**
 * Resolve a single whitespace-delimited token in a mixed className string.
 *  - `@ds:X` → DS resolver (unresolved → warning + original token kept).
 *  - `#{...}` → expression evaluator, coerced to string for join (undefined/null → empty).
 *  - anything else → returned verbatim.
 */
function resolveToken(tok: string, r: Resolvers, warnings: string[]): string {
  if (tok.startsWith("@ds:")) {
    const name = tok.slice("@ds:".length)
    const resolved = r.__ds.resolveTokenName(name)
    if (resolved === undefined) {
      warnings.push(`unresolved DS token: ${tok}`)
      return tok
    }
    return resolved
  }
  if (tok.startsWith("#{") && tok.endsWith("}")) {
    const v = evaluateExpression(tok, r, warnings)
    if (v === undefined || v === null) return ""
    return String(v)
  }
  return tok
}
